import type {PublishedProductCatalogSnapshot} from '../../../packages/catalog/product-catalog.ts';
import type {createBusinessRuntime} from '../../../packages/runtime/business-runtime.ts';
import type {ProductOptionSelectionInput, ProductSelectionInput} from '../../../packages/order/product-selection.ts';

type BusinessRuntime = ReturnType<typeof createBusinessRuntime>;
type RuntimeCustomerProductProjection = NonNullable<ReturnType<BusinessRuntime['customerProductProjection']>>;
type NextId = (kind: 'line' | 'submission' | 'idempotency') => string;

export interface CustomerPaymentSubmission {
  readonly mode: 'pay-at-store' | 'electronic-proof';
  readonly evidenceRef?: string;
}

export interface CustomerComboProductItem {
  readonly id: string;
  readonly categoryId: string;
  readonly name: string;
  readonly priceLabel: string;
  readonly subtitle?: string;
  readonly action: 'direct' | 'configure' | 'disabled';
}

export interface CustomerComboConfiguration {
  readonly productId: string;
  readonly productName: string;
  readonly groups: readonly {
    readonly optionGroupId: string;
    readonly label: string;
    readonly required: boolean;
    readonly min: number;
    readonly max: number;
    readonly options: readonly {readonly optionId: string; readonly label: string; readonly enabled: boolean; readonly priceDeltaLabel?: string}[];
  }[];
}

export interface CustomerComboSnapshot {
  readonly catalogRevision: string;
  readonly categories: readonly {readonly id: string; readonly label: string}[];
  readonly products: readonly CustomerComboProductItem[];
  readonly lines: readonly {readonly id: string; readonly productId: string; readonly name: string; readonly quantity: number}[];
  readonly countLabel: string;
  readonly totalLabel: string;
  readonly quoteStatus: 'empty' | 'quoted' | 'blocked' | 'rejected' | 'unbound';
  readonly combo: {
    readonly ruleLabels: readonly string[];
    readonly savingLabel?: string;
  };
}

export interface CustomerComboPortConfig {
  readonly runtime: BusinessRuntime;
  readonly catalog: PublishedProductCatalogSnapshot;
  readonly businessDate: () => string;
  readonly now: () => string;
  readonly nextId: NextId;
}

const formatMoney = (currency: string, minor: number): string => new Intl.NumberFormat('zh-HK', {
  style: 'currency', currency, currencyDisplay: 'narrowSymbol', minimumFractionDigits: 0, maximumFractionDigits: 2,
}).format(minor / 100);

const requireEvidenceRef = (value: string | undefined): string => {
  const normalized = value?.trim() ?? '';
  if (!normalized) throw new Error('CUSTOMER_PAYMENT_EVIDENCE_REQUIRED');
  return normalized;
};

export function createCustomerComboPort(config: CustomerComboPortConfig) {
  const listeners = new Set<() => void>();
  let draftLines: ProductSelectionInput[] = [];

  const customerProjection = (productId: string): RuntimeCustomerProductProjection | null =>
    config.runtime.customerProductProjection(productId);

  const catalogEntries = () => config.catalog.payload.products
    .filter((entry) => entry.product.status === 'active' && entry.eligibleChannels.includes('customer-web'))
    .map((entry) => ({entry, projection: customerProjection(entry.product.id)}))
    .filter((item): item is {entry: typeof item.entry; projection: RuntimeCustomerProductProjection} => Boolean(item.projection?.visible));

  const preview = () => draftLines.length === 0 ? null : config.runtime.previewDraft(draftLines);

  const buildSnapshot = (): CustomerComboSnapshot => {
    const entries = catalogEntries();
    const categoryIds = new Set(entries.map(({entry}) => entry.categoryId));
    const categories = [...config.catalog.payload.categories]
      .filter((category) => category.status === 'active' && categoryIds.has(category.categoryId))
      .sort((a, b) => a.order - b.order)
      .map((category) => ({id: category.categoryId, label: category.names.customer}));

    const products: CustomerComboProductItem[] = entries.map(({entry, projection}) => ({
      id: projection.productId,
      categoryId: entry.categoryId,
      name: projection.name,
      priceLabel: projection.basePriceMinor === null ? '—' : formatMoney(projection.currency, projection.basePriceMinor),
      ...(entry.description ? {subtitle: entry.description} : {}),
      action: !projection.sellable ? 'disabled' : projection.requiresConfiguration || projection.hasOptions ? 'configure' : 'direct',
    }));

    const currentPreview = preview();
    const quote = currentPreview?.quote ?? null;
    let totalLabel = '—';
    let quoteStatus: CustomerComboSnapshot['quoteStatus'] = 'empty';
    let ruleLabels: readonly string[] = [];
    let savingLabel: string | undefined;
    if (quote) {
      quoteStatus = quote.kind;
      if (quote.kind === 'quoted') {
        totalLabel = formatMoney(quote.evidence.pricing.currency, quote.evidence.pricing.totalMinor);
        ruleLabels = quote.comboMatches.map((match) => match.ruleId);
        const savingMinor = quote.evidence.pricing.adjustments
          .filter((adjustment) => adjustment.kind === 'combo' && adjustment.amountMinor < 0)
          .reduce((sum, adjustment) => sum - adjustment.amountMinor, 0);
        if (savingMinor > 0) savingLabel = formatMoney(quote.evidence.pricing.currency, savingMinor);
      }
    }

    const names = new Map(entries.map(({projection}) => [projection.productId, projection.name]));
    return {
      catalogRevision: config.catalog.catalogRevision,
      categories,
      products,
      lines: draftLines.map((line) => ({
        id: line.lineId,
        productId: line.productId,
        name: names.get(line.productId) ?? line.productId,
        quantity: line.quantity,
      })),
      countLabel: String(draftLines.reduce((sum, line) => sum + line.quantity, 0)),
      totalLabel,
      quoteStatus,
      combo: {ruleLabels, ...(savingLabel ? {savingLabel} : {})},
    };
  };

  let snapshot = buildSnapshot();
  const publish = () => {
    snapshot = buildSnapshot();
    for (const listener of listeners) listener();
  };

  const requireSellable = (productId: string) => {
    const projection = customerProjection(productId);
    if (!projection?.visible || !projection.sellable) throw new Error('CUSTOMER_PRODUCT_NOT_SELLABLE');
    return projection;
  };

  const directAdd = (productId: string): void => {
    const projection = requireSellable(productId);
    if (projection.requiresConfiguration || projection.hasOptions) throw new Error('CUSTOMER_CONFIGURATION_REQUIRED');
    const existing = draftLines.find((line) => line.productId === productId && line.selections.length === 0);
    draftLines = existing
      ? draftLines.map((line) => line.lineId === existing.lineId ? {...line, quantity: line.quantity + 1} : line)
      : [...draftLines, {lineId: config.nextId('line'), productId, quantity: 1, selections: []}];
    publish();
  };

  const configuredAdd = (productId: string, selections: readonly ProductOptionSelectionInput[]): void => {
    requireSellable(productId);
    draftLines = [...draftLines, {lineId: config.nextId('line'), productId, quantity: 1, selections: selections.map((selection) => ({...selection, optionIds: [...selection.optionIds]}))}];
    publish();
  };

  const changeQuantity = (lineId: string, delta: number): void => {
    if (!Number.isSafeInteger(delta) || delta === 0) throw new Error('CUSTOMER_QUANTITY_DELTA_INVALID');
    const line = draftLines.find((candidate) => candidate.lineId === lineId);
    if (!line) throw new Error('CUSTOMER_CART_LINE_NOT_FOUND');
    const quantity = line.quantity + delta;
    draftLines = quantity <= 0
      ? draftLines.filter((candidate) => candidate.lineId !== lineId)
      : draftLines.map((candidate) => candidate.lineId === lineId ? {...candidate, quantity} : candidate);
    publish();
  };

  const configurationFor = (productId: string): CustomerComboConfiguration | null => {
    const projection = customerProjection(productId);
    if (!projection?.visible) return null;
    return {
      productId,
      productName: projection.name,
      groups: projection.optionGroups.map((group) => ({
        optionGroupId: group.optionGroupId,
        label: group.name,
        required: group.required,
        min: group.min,
        max: group.max,
        options: group.options.map((option) => ({
          optionId: option.optionId,
          label: option.name,
          enabled: option.enabled,
          ...(option.priceDeltaMinor !== null && option.priceDeltaMinor !== 0 ? {priceDeltaLabel: formatMoney(projection.currency, option.priceDeltaMinor)} : {}),
        })),
      })),
    };
  };

  const submit = (payment?: CustomerPaymentSubmission) => {
    if (!payment) throw new Error('CUSTOMER_PAYMENT_MODE_REQUIRED');
    if (payment.mode !== 'pay-at-store' && payment.mode !== 'electronic-proof') throw new Error('CUSTOMER_PAYMENT_MODE_INVALID');
    const evidenceRef = payment.mode === 'electronic-proof'
      ? requireEvidenceRef(payment.evidenceRef)
      : undefined;
    if (payment.mode === 'pay-at-store' && payment.evidenceRef?.trim()) {
      throw new Error('CUSTOMER_PAYMENT_EVIDENCE_NOT_ALLOWED');
    }
    if (draftLines.length === 0) throw new Error('CUSTOMER_CART_EMPTY');
    const currentPreview = config.runtime.previewDraft(draftLines);
    if (currentPreview.quote?.kind !== 'quoted') throw new Error(`CUSTOMER_QUOTE_${currentPreview.quote?.kind?.toUpperCase() ?? 'REQUIRED'}`);
    const submissionId = config.nextId('submission');
    const quote = currentPreview.quote;
    const result = config.runtime.submitChannel({
      submissionId,
      idempotencyKey: config.nextId('idempotency'),
      source: 'customer',
      channel: 'customer-web',
      createdAt: config.now(),
      businessDate: config.businessDate(),
      payload: {
        lines: draftLines.map((line) => ({
          lineId: line.lineId,
          productId: line.productId,
          quantity: line.quantity,
          selections: line.selections.map((selection) => ({optionGroupId: selection.optionGroupId, optionIds: [...selection.optionIds]})),
          ...(line.note !== null && line.note !== undefined ? {note: line.note} : {}),
        })),
        customerDisplayedTotalMinor: quote.evidence.pricing.totalMinor,
        currency: quote.evidence.pricing.currency,
        paymentMode: payment.mode,
        ...(evidenceRef ? {paymentEvidenceRef: evidenceRef} : {}),
        externalReference: submissionId,
      },
    });
    draftLines = [];
    publish();
    return result;
  };

  const resubmitPaymentProof = (submissionId: string, evidenceRef: string) => {
    const proof = config.runtime.channelPaymentProof(submissionId);
    if (!proof || proof.status !== 'resubmission_required') {
      throw new Error('CUSTOMER_PAYMENT_PROOF_RESUBMISSION_NOT_REQUIRED');
    }
    return config.runtime.resubmitChannelPaymentProof(submissionId, {
      expectedVersion: proof.version,
      idempotencyKey: config.nextId('idempotency'),
      evidenceRef: requireEvidenceRef(evidenceRef),
      submittedAt: config.now(),
      submittedBy: 'customer',
    });
  };

  return {
    getSnapshot: () => snapshot,
    subscribe(listener: () => void) { listeners.add(listener); return () => listeners.delete(listener); },
    refresh() { publish(); return snapshot; },
    customerProjection,
    configurationFor,
    directAdd,
    configuredAdd,
    changeQuantity,
    submit,
    paymentProofStatus: (submissionId: string) => config.runtime.channelPaymentProof(submissionId),
    resubmitPaymentProof,
  };
}

export type CustomerComboPort = ReturnType<typeof createCustomerComboPort>;
